import Phaser from 'phaser';

/**
 * SVG 문자열을 Phaser Texture로 등록.
 * 비동기 — Promise resolve 시 textureKey 사용 가능.
 */
/**
 * SVG의 viewBox에서 width/height를 추출. 없으면 [300, 150] (브라우저 default).
 */
function parseViewBox(svg: string): [number, number] {
  const m = svg.match(/viewBox="([\d.\s-]+)"/);
  if (!m) return [300, 150];
  const parts = m[1].trim().split(/\s+/).map(Number);
  if (parts.length !== 4) return [300, 150];
  return [parts[2], parts[3]];
}

/**
 * SVG에 명시적 width/height attribute가 없으면 viewBox 기준으로 추가.
 * 이게 없으면 Image()가 default 300x150으로 렌더링되어 sprite 크기가 엉뚱해짐.
 */
function ensureSvgDimensions(svg: string): string {
  if (/<svg[^>]*\swidth=/.test(svg) && /<svg[^>]*\sheight=/.test(svg)) return svg;
  const [w, h] = parseViewBox(svg);
  return svg.replace(/<svg/, `<svg width="${w}" height="${h}"`);
}

export function svgStringToTexture(
  scene: Phaser.Scene,
  textureKey: string,
  svgString: string,
  timeoutMs = 5000,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (scene.textures.exists(textureKey)) {
      scene.textures.remove(textureKey);
    }
    const sized = ensureSvgDimensions(svgString);
    const blob = new Blob([sized], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    const [w, h] = parseViewBox(sized);
    img.width = w;
    img.height = h;

    let settled = false;
    const cleanup = (): void => {
      URL.revokeObjectURL(url);
      img.onload = null;
      img.onerror = null;
    };

    // 타임아웃 — 이미지가 절대 load되지 않으면 promise hang 방지
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(`SVG load timeout (${timeoutMs}ms): ${textureKey}`));
    }, timeoutMs);

    img.onload = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        scene.textures.addImage(textureKey, img);
      } catch (e) {
        cleanup();
        reject(e);
        return;
      }
      cleanup();
      resolve();
    };
    img.onerror = (e) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(new Error(`Failed to load SVG: ${textureKey} (${e instanceof Event ? 'event' : String(e)})`));
    };
    img.src = url;
  });
}
